import { useAuth } from "@dl/web-core-js/auth";
import { Card, Field, Input, Select, Switch, entityOptions } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  INVENTORY_POLICIES,
  PART_STATUSES,
  PART_TYPES,
  type DepartmentDTO,
  type PartDTO,
  type PartEdgebandDTO,
  type PartGroupDTO,
  type UnitDTO,
  BOM_SUPPLIES,
  type BomSupply,
} from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import { bomSupplyLabel } from "./bomSupply";
import { isoDate } from "../../api/reporting";

/**
 * Identity, classification and the handling rules.
 *
 * The split between editable and read-only follows where each value comes
 * from, not what looks tidy:
 *
 *   - Number, name, codes: entered by a human, editable.
 *   - The edge pattern is read off the banded sides (Edgebands tab); painted,
 *     edged by hand and the machines are the routing's; a kanban part is a made
 *     part supplied from stock; the bin is each stocking point's (0062).
 *   - Group, unit, department: references to other tables, so pickers.
 *   - Created date: stamped at creation. Inactivated date is shown only when
 *     the part is inactive, because the schema refuses it otherwise.
 *   - Lead time and purchase multiple are NOT here either: they belong to the
 *     part-supplier link (one supplier's bundle is not another's), on the
 *     Suppliers tab. What is here is where the part is used up — its
 *     consumption warehouse — because that is where it is planned and received.
 *   - The dimensions are NOT here. They live in `part_measurement`, one row
 *     per kind with its own unit, and get their own tab — flattening them back
 *     into three boxes here is exactly the shape we left SIGM to escape.
 */
type Translate = (key: string, fallback: string) => string;

/** "Made" / "Bought", the words the plant uses, for a part's type; "Virtual panel" for an engineering material. */
export function partTypeLabel(type: string, t: Translate): string {
  switch (type) {
    case "MANUFACTURED":
      return t("part.type.manufactured", "Made");
    case "VIRTUAL":
      return t("part.type.virtual", "Virtual part");
    default:
      return t("part.type.purchased", "Bought");
  }
}

export function partStatusLabel(status: string, t: Translate): string {
  return status === "ACTIVE" ? t("status.active", "Active") : t("status.inactive", "Inactive");
}

export function inventoryPolicyLabel(policy: string, t: Translate): string {
  switch (policy) {
    case "TRACKED":
      return t("part.inventory-policy.tracked", "Tracked");
    case "UNTRACKED":
      return t("part.inventory-policy.untracked", "Not tracked");
    default:
      return policy;
  }
}

/** A whole number of 0 or more, or blank; anything else is what the field says is wrong. */
function countError(value: number | null, t: Translate): string | undefined {
  if (value === null) return undefined;
  return Number.isInteger(value) && value >= 0 ? undefined : t("validation.whole-number", "A whole number, 0 or more.");
}

/** A whole number of at least 1, or blank: parts per cutboard and per pallet. */
function positiveError(value: number | null): boolean {
  return value !== null && !(Number.isInteger(value) && value >= 1);
}

/** Whether the draft holds a value the form flags; Save stays off until it is fixed. */
export function partDraftInvalid(draft: PartDTO): boolean {
  const count = (value: number | null) => value !== null && !(Number.isInteger(value) && value >= 0);
  return (
    draft.number.trim() === "" ||
    draft.name.trim() === "" ||
    (draft.production_multiple !== null && !(draft.production_multiple > 0)) ||
    count(draft.hole_count) ||
    positiveError(draft.parts_per_cutboard ?? null) ||
    positiveError(draft.pallet_quantity ?? null)
  );
}

export function PartGeneralTab({
  draft,
  onChange,
  readOnly,
  groups,
  units,
  departments,
  edgebands,
}: {
  draft: PartDTO;
  edgebands: PartEdgebandDTO[];
  onChange: (next: PartDTO) => void;
  readOnly: boolean;
  groups: PartGroupDTO[];
  units: UnitDTO[];
  departments: DepartmentDTO[];
}) {
  const { t } = useTranslation();
  const { http } = useAuth();

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });
  // Never a distribution warehouse: it holds Nexera's sellable stock, and the
  // database refuses it as a consumption location.
  const consumptionOptions = (warehouses.data ?? [])
    .filter((w) => w.kind !== "DISTRIBUTION" && (w.is_active || w.id === draft.consumption_warehouse_id))
    .map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }));

  const set = <K extends keyof PartDTO>(key: K, value: PartDTO[K]) =>
    onChange({ ...draft, [key]: value });

  const text = (value: string) => (value.trim() === "" ? null : value);

  const groupOptions = groups
    .filter((group) => group.is_active || group.id === draft.part_group_id)
    .map((group) => ({ value: String(group.id), label: `${group.code} · ${group.name}` }));

  const unitOptions = units
    .filter((unit) => unit.is_active || unit.id === draft.stock_unit_id)
    .map((unit) => ({
      value: String(unit.id),
      label: unit.base_factor === null ? `${unit.code} (${t("unit.per-part", "per part")})` : unit.code,
    }));

  const departmentOptions = entityOptions(
    departments.filter((department) => department.is_active || department.id === draft.department_id),
  );

  const group = groups.find((candidate) => candidate.id === draft.part_group_id);
  const isFinished = group?.kind === "FINISHED";
  // Only a made part has a bill, a routing, cutting and production figures: a
  // bought or virtual part shows none of them.
  const made = draft.type === "MANUFACTURED";
  const pattern = edgePattern(edgebands);

  return (
    <div className="flex flex-col gap-4">
      <Section title={t("part.section.identity", "Identity")}>
        <Field
          label={t("form.field.part-number", "Part number")}
          htmlFor="part-number"
          required
          error={draft.number.trim() === "" ? t("validation.required", "Required") : undefined}
        >
          <Input
            id="part-number"
            value={draft.number}
            invalid={draft.number.trim() === ""}
            disabled={readOnly}
            onChange={(event) => set("number", event.target.value)}
          />
        </Field>

        <Field
          label={t("form.field.name", "Name")}
          htmlFor="part-name"
          required
          className="lg:col-span-2"
          error={draft.name.trim() === "" ? t("validation.required", "Required") : undefined}
        >
          <Input
            id="part-name"
            value={draft.name}
            invalid={draft.name.trim() === ""}
            disabled={readOnly}
            onChange={(event) => set("name", event.target.value)}
          />
        </Field>

        <Field label={t("form.field.description", "Description")} htmlFor="part-description" className="lg:col-span-3">
          <Input
            id="part-description"
            value={draft.description ?? ""}
            disabled={readOnly}
            onChange={(event) => set("description", text(event.target.value))}
          />
        </Field>

        <Field label={t("form.field.part-group", "Group")} htmlFor="part-group">
          <Select
            id="part-group"
            value={draft.part_group_id === null ? "" : String(draft.part_group_id)}
            placeholder={t("common.none", "None")}
            options={groupOptions}
            disabled={readOnly}
            onChange={(event) =>
              set("part_group_id", event.target.value === "" ? null : Number(event.target.value))
            }
          />
        </Field>

        <Field label={t("form.field.type", "Type")} htmlFor="part-type">
          <Select
            id="part-type"
            value={draft.type}
            // A virtual part is created from the Virtual parts page, with what it is made of.
            options={PART_TYPES.filter((type) => type !== "VIRTUAL" || draft.type === "VIRTUAL").map((type) => ({ value: type, label: partTypeLabel(type, t) }))}
            disabled={readOnly}
            onChange={(event) => set("type", event.target.value as PartDTO["type"])}
          />
        </Field>

        <Field label={t("form.field.status", "Status")} htmlFor="part-status">
          <Select
            id="part-status"
            value={draft.status}
            options={PART_STATUSES.map((status) => ({ value: status, label: partStatusLabel(status, t) }))}
            disabled={readOnly}
            onChange={(event) => {
              const status = event.target.value as PartDTO["status"];
              // The schema allows an inactivation date only on an inactive
              // part, so clearing the status has to clear the date with it —
              // otherwise Save fails on a constraint the user never saw.
              onChange({
                ...draft,
                status,
                inactivated_date:
                  status === "ACTIVE"
                    ? null
                    : (draft.inactivated_date ?? isoDate(new Date())),
              });
            }}
          />
        </Field>

        <Field label={t("form.field.department", "Department")} htmlFor="part-department">
          <Select
            id="part-department"
            value={draft.department_id === null ? "" : String(draft.department_id)}
            placeholder={t("common.none", "None")}
            options={departmentOptions}
            disabled={readOnly}
            onChange={(event) =>
              set("department_id", event.target.value === "" ? null : Number(event.target.value))
            }
          />
        </Field>

        <Field label={t("form.field.created-date", "Created")} htmlFor="part-created">
          <Input id="part-created" value={draft.created_date ?? ""} disabled />
        </Field>

        {draft.status === "INACTIVE" && (
          <Field label={t("form.field.inactivated-date", "Inactivated")} htmlFor="part-inactivated">
            <Input
              id="part-inactivated"
              type="date"
              value={draft.inactivated_date ?? ""}
              disabled={readOnly}
              onChange={(event) => set("inactivated_date", text(event.target.value))}
            />
          </Field>
        )}
      </Section>

      <Section title={t("part.section.stock", "Stock")}>
        <Field
          label={t("form.field.stock-unit", "Stock unit")}
          htmlFor="part-unit"
          hint={
            units.find((unit) => unit.id === draft.stock_unit_id)?.base_factor === null
              ? t("part.unit.needs-factor", "This unit needs a per-part quantity: set it on the Units tab.")
              : undefined
          }
        >
          <Select
            id="part-unit"
            value={String(draft.stock_unit_id)}
            options={unitOptions}
            disabled={readOnly}
            onChange={(event) => set("stock_unit_id", Number(event.target.value))}
          />
        </Field>

        <Field
          label={t("form.field.inventory-policy", "Inventory policy")}
          htmlFor="part-policy"
          hint={t(
            "part.policy.hint.tracked",
            "Tracked: receptions and production add, usage subtracts, it is counted. Not tracked: expensed when received, never held as stock.",
          )}
        >
          <Select
            id="part-policy"
            value={draft.inventory_policy}
            options={INVENTORY_POLICIES.map((policy) => ({ value: policy, label: inventoryPolicyLabel(policy, t) }))}
            disabled={readOnly}
            onChange={(event) =>
              set("inventory_policy", event.target.value as PartDTO["inventory_policy"])
            }
          />
        </Field>

        <Field
          label={t("part.flag.consumed-whole", "Consumed whole (panels)")}
          hint={t("part.consumed-whole.hint", "A lot uses it up in whole units: a sheet started is a sheet used.")}
        >
          <Switch
            checked={draft.is_consumed_whole ?? false}
            disabled={readOnly}
            onChange={(checked) => set("is_consumed_whole", checked)}
            label={draft.is_consumed_whole ? t("common.yes", "Yes") : t("common.no", "No")}
          />
        </Field>

        {made && (
          <Field
            label={t("form.field.supply", "Supply in a parent's BOM")}
            htmlFor="part-supply"
            hint={t(
              "part.supply.hint.kanban",
              "How an order that uses this part gets it: its materials directly (phantom), a child order made for the order, or from stock. A made part from stock is a kanban part: it is run on its own.",
            )}
          >
            <Select
              id="part-supply"
              value={draft.supply ?? ""}
              options={[
                {
                  value: "",
                  label: t("part.supply.group", "As its group: {{supply}}", {
                    supply: bomSupplyLabel(group?.default_supply ?? "PHANTOM", t),
                  }),
                },
                ...BOM_SUPPLIES.map((supply) => ({ value: supply, label: bomSupplyLabel(supply, t) })),
              ]}
              disabled={readOnly}
              onChange={(event) => set("supply", event.target.value === "" ? null : (event.target.value as BomSupply))}
            />
          </Field>
        )}

        {/* Set on the Stocked at tab: the part's consumption stocking point. */}
        <Field
          label={t("form.field.consumption-warehouse", "Used up at")}
          htmlFor="part-consumption"
          hint={t("part.consumption.hint.points", "Its consumption point, set on the Stocked at tab. Blank: its group's, else where the order runs.")}
        >
          <Select
            id="part-consumption"
            value={draft.consumption_warehouse_id === null ? "" : String(draft.consumption_warehouse_id)}
            disabled
            placeholder={t("part.consumption.default", "Where the order runs")}
            options={consumptionOptions}
            onChange={() => undefined}
          />
        </Field>
      </Section>

      {made && (
        <Section title={t("part.section.cutting", "Cutting and machining")}>
          <Field
            label={t("form.field.edge-pattern", "Edge pattern")}
            htmlFor="part-edge-pattern"
            hint={t("part.edge-pattern.hint", "Read off the banded sides, set on the Edgebands tab: L a long side, C a short one, .5 a side banded in part.")}
          >
            <Input id="part-edge-pattern" value={pattern ?? t("part.edge-pattern.none", "No band")} disabled />
          </Field>

          <Field
            label={t("form.field.parts-per-cutboard", "Parts per cutboard")}
            htmlFor="part-per-cutboard"
            hint={t("part.per-cutboard.hint", "Nested on one board through the saw and the edgebander, split after.")}
            error={positiveError(draft.parts_per_cutboard ?? null) ? t("validation.whole-number-one", "A whole number, 1 or more.") : undefined}
          >
            <Input
              id="part-per-cutboard"
              type="number"
              min={1}
              step={1}
              value={draft.parts_per_cutboard ?? ""}
              disabled={readOnly}
              onChange={(event) =>
                set("parts_per_cutboard", event.target.value === "" ? null : Number(event.target.value))
              }
            />
          </Field>

          <Field label={t("form.field.hole-count", "Holes")} htmlFor="part-holes" error={countError(draft.hole_count, t)}>
            <Input
              id="part-holes"
              type="number"
              min={0}
              step={1}
              value={draft.hole_count ?? ""}
              disabled={readOnly}
              onChange={(event) =>
                set("hole_count", event.target.value === "" ? null : Number(event.target.value))
              }
            />
          </Field>
        </Section>
      )}

      <Section title={t("part.section.production", "Production")}>
        {made && (
          <>
            <Field
              label={t("part.has-routing", "Routing")}
              hint={t("part.has-routing.hint", "Made in steps of its own (a cut piece, an assembly version): its BOM page shows the routing. Off for a semi-finished, a hardware bag, a foam pad cut at the assembly step.")}
            >
              <Switch
                checked={draft.has_routing ?? false}
                disabled={readOnly}
                onChange={(checked) => set("has_routing", checked)}
                label={draft.has_routing ? t("part.has-routing.on", "Has a routing") : t("part.has-routing.off", "No routing")}
              />
            </Field>
            <Field
              label={t("form.field.drawing-program", "Drawing / CNC program")}
              htmlFor="part-drawing"
              hint={t("part.drawing.hint", "Printed on the drawings; a derived machining step takes it as its program.")}
            >
              <Input
                id="part-drawing"
                value={draft.drawing_number ?? ""}
                disabled={readOnly}
                onChange={(event) => set("drawing_number", text(event.target.value))}
              />
            </Field>
          </>
        )}

        <Field
          label={t("form.field.letter", "Letter")}
          htmlFor="part-letter"
          hint={t("part.letter.hint", "What the floor and the assembly guide call it: G1, X1.")}
        >
          <Input
            id="part-letter"
            value={draft.letter ?? ""}
            disabled={readOnly}
            onChange={(event) => set("letter", text(event.target.value))}
          />
        </Field>
        {made && (
          <>
            <Field
              label={t("form.field.production-multiple", "Production multiple")}
              htmlFor="part-production-multiple"
              error={
                draft.production_multiple !== null && !(draft.production_multiple > 0)
                  ? t("validation.positive", "Must be greater than zero")
                  : undefined
              }
            >
              <Input
                id="part-production-multiple"
                type="number"
                min={0}
                step="any"
                value={draft.production_multiple ?? ""}
                disabled={readOnly}
                onChange={(event) =>
                  set("production_multiple", event.target.value === "" ? null : Number(event.target.value))
                }
              />
            </Field>

            <Field
              label={t("form.field.pallet-quantity", "Parts per pallet")}
              htmlFor="part-per-pallet"
              hint={t("part.per-pallet.hint", "One drawing is printed per pallet. Blank: no limit.")}
              error={positiveError(draft.pallet_quantity ?? null) ? t("validation.whole-number-one", "A whole number, 1 or more.") : undefined}
            >
              <Input
                id="part-per-pallet"
                type="number"
                min={1}
                step={1}
                value={draft.pallet_quantity ?? ""}
                disabled={readOnly}
                onChange={(event) =>
                  set("pallet_quantity", event.target.value === "" ? null : Number(event.target.value))
                }
              />
            </Field>

            {(isFinished || draft.service_label_letter) && (
              <Field
                label={t("form.field.service-label-letter", "Service label on")}
                htmlFor="part-label-letter"
                hint={t("part.label-letter.hint", "Finished products: the letter of the component that gets the customer-service label.")}
              >
                <Input
                  id="part-label-letter"
                  value={draft.service_label_letter ?? ""}
                  disabled={readOnly}
                  onChange={(event) => set("service_label_letter", text(event.target.value))}
                />
              </Field>
            )}
          </>
        )}
      </Section>

      <Section title={t("part.section.customs", "Customs")}>
        <Field label={t("form.field.upc", "UPC")} htmlFor="part-upc">
          <Input
            id="part-upc"
            value={draft.upc ?? ""}
            disabled={readOnly}
            onChange={(event) => set("upc", text(event.target.value))}
          />
        </Field>

        <Field label={t("form.field.harmonized-code", "HS code")} htmlFor="part-hs">
          <Input
            id="part-hs"
            value={draft.harmonized_code ?? ""}
            disabled={readOnly}
            onChange={(event) => set("harmonized_code", text(event.target.value))}
          />
        </Field>

        <Field
          label={t("form.field.customs-description", "Customs description")}
          htmlFor="part-customs"
        >
          <Input
            id="part-customs"
            value={draft.customs_description ?? ""}
            disabled={readOnly}
            onChange={(event) => set("customs_description", text(event.target.value))}
          />
        </Field>
      </Section>

    </div>
  );
}

/** A titled card of fields, three across on a wide screen. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="p-4 sm:p-5">
      <h3 className="mb-3 text-[11px] font-medium uppercase tracking-wide text-ink-muted">{title}</h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </Card>
  );
}

/**
 * The plant's edge shorthand, read off the banded sides (0062): TOP and BOTTOM
 * are the long sides (L), RIGHT and LEFT the short ones (C), a partial side
 * counts half. One long and two short sides is "1L 2C". No band: null.
 */
export function edgePattern(sides: readonly { edge: string; is_partial?: boolean }[]): string | null {
  let longs = 0;
  let shorts = 0;
  for (const side of sides) {
    const amount = side.is_partial ? 0.5 : 1;
    if (side.edge === "TOP" || side.edge === "BOTTOM") longs += amount;
    else shorts += amount;
  }
  const parts = [longs > 0 ? `${longs}L` : null, shorts > 0 ? `${shorts}C` : null].filter(Boolean);
  return parts.length === 0 ? null : parts.join(" ");
}
